import type {
  AttainmentTrendPeriod,
  AttainmentTrendResult,
  PeriodAverageScoreDeltas,
  PeriodAverageScores,
} from "@repo/app-types/entities/attainmentTrend";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  getCriteriaTrendMovementCategory,
  type CriteriaAttainmentTrendMovementCategory,
} from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { ChartSkeleton, skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { IconArrowDownRight, IconArrowUpRight } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../../app/appPaths.js";
import { CriterionAverageScore } from "../criterionAverageScore.js";
import {
  computeScoreAxisRange,
  formatScore,
  formatScoreAxisLabel,
  formatScoreDelta,
  ScoreMovementScatterChart,
  type ScoreMovementPoint,
} from "../scoreMovementScatterChart.js";

interface TeammateScoreMovement {
  id: string;
  name: string;
  startScore: number;
  currentScore: number;
  delta: number;
}

const PERIOD_OPTIONS: { value: AttainmentTrendPeriod; label: string }[] = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "last_180_days", label: "Last 180 days" },
];

interface DashboardCriteriaAttainmentCardProps {
  readonly criterionName: string;
  readonly criterionId: string;
  readonly gradingValueType: "yes_no" | "1_5_score";
  readonly attainment: AttainmentTrendResult | undefined;
  readonly averageScore: PeriodAverageScores | undefined;
  readonly averageScoreDelta: PeriodAverageScoreDeltas | undefined;
  readonly teammates: readonly Teammate[];
  readonly isLoading: boolean;
  readonly showPeriodDropdown: boolean;
  readonly onSelectedPeriodChange?: (period: AttainmentTrendPeriod) => void;
}

export const DashboardCriteriaAttainmentCard: FC<
  DashboardCriteriaAttainmentCardProps
> = ({
  criterionName,
  criterionId,
  gradingValueType,
  attainment,
  averageScore,
  averageScoreDelta,
  teammates,
  isLoading,
  showPeriodDropdown,
  onSelectedPeriodChange,
}) => {
  const navigate = useNavigate();
  const { playbookId } = useParams();
  const [selectedPeriod, setSelectedPeriod] =
    useState<AttainmentTrendPeriod>("last_30_days");
  const handleSelectedPeriodChange = (period: AttainmentTrendPeriod) => {
    setSelectedPeriod(period);
    onSelectedPeriodChange?.(period);
  };
  const [visibleCategories, setVisibleCategories] = useState<
    Record<CriteriaAttainmentTrendMovementCategory, boolean>
  >({
    improving: true,
    flat: true,
    declining: true,
  });

  const toggleCategory = (
    category: CriteriaAttainmentTrendMovementCategory,
  ) => {
    setVisibleCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const effectiveAverageScore = averageScore?.[selectedPeriod];
  const effectiveDelta = averageScoreDelta?.[selectedPeriod];

  const teammatesMap = useMemo(() => {
    return teammates.reduce<Record<TeammateId, Teammate>>((acc, teammate) => {
      acc[teammate.id] = teammate;
      return acc;
    }, {});
  }, [teammates]);

  const formatAvgDelta = (avgDelta: number): string => {
    if (gradingValueType === "yes_no") {
      return `${avgDelta >= 0 ? "+" : ""}${Math.round(avgDelta * 100)} pts avg`;
    }
    return `${avgDelta >= 0 ? "+" : ""}${avgDelta.toFixed(1)} avg`;
  };

  const teammateScoreMovements =
    useMemo((): readonly TeammateScoreMovement[] => {
      const result: TeammateScoreMovement[] = [];
      for (const [teammateId, trends] of recordEntries(attainment ?? {})) {
        const period = trends[selectedPeriod];
        if (!period) continue;
        const startScore = period.starting_score;
        const currentScore = period.current_score;
        if (startScore === null || currentScore === null) continue;

        const teammate = teammatesMap[teammateId];
        const displayName = teammate
          ? buildTeammateDisplayNames(teammate).primaryDisplayName
          : teammateId;

        result.push({
          id: teammateId,
          name: displayName,
          startScore,
          currentScore,
          delta: currentScore - startScore,
        });
      }
      return result;
    }, [attainment, selectedPeriod, teammatesMap]);

  const categoryStats = useMemo(() => {
    const stats = {
      improving: { count: 0, totalDelta: 0 },
      flat: { count: 0, totalDelta: 0 },
      declining: { count: 0, totalDelta: 0 },
    };
    for (const t of teammateScoreMovements) {
      const category = getCriteriaTrendMovementCategory(
        t.delta,
        gradingValueType,
      );
      stats[category].count++;
      stats[category].totalDelta += t.delta;
    }
    return {
      improving: {
        count: stats.improving.count,
        avgDelta:
          stats.improving.count > 0
            ? stats.improving.totalDelta / stats.improving.count
            : 0,
      },
      flat: { count: stats.flat.count },
      declining: {
        count: stats.declining.count,
        avgDelta:
          stats.declining.count > 0
            ? stats.declining.totalDelta / stats.declining.count
            : 0,
      },
    };
  }, [teammateScoreMovements, gradingValueType]);

  const { axisMin, axisMax, axisTicks } = useMemo(
    () => computeScoreAxisRange(teammateScoreMovements, gradingValueType),
    [teammateScoreMovements, gradingValueType],
  );

  const chartPoints = useMemo((): readonly ScoreMovementPoint[] => {
    return teammateScoreMovements
      .map((teammate): ScoreMovementPoint | null => {
        const category = getCriteriaTrendMovementCategory(
          teammate.delta,
          gradingValueType,
        );
        if (!visibleCategories[category]) return null;

        return {
          id: teammate.id,
          startScore: teammate.startScore,
          currentScore: teammate.currentScore,
          category,
          tooltipContent: `${teammate.name}\nBefore: ${formatScore(teammate.startScore, gradingValueType)}\nAfter: ${formatScore(teammate.currentScore, gradingValueType)}\nDelta: ${formatScoreDelta(teammate.delta, gradingValueType)}`,
          onClick: () => {
            if (playbookId) {
              navigate(
                appPaths.teammateCriterion.build({
                  playbookId,
                  teammateId: teammate.id,
                  criterionId,
                }),
              );
            }
          },
        };
      })
      .filter((p): p is ScoreMovementPoint => p !== null);
  }, [
    teammateScoreMovements,
    visibleCategories,
    gradingValueType,
    navigate,
    playbookId,
    criterionId,
  ]);

  return (
    <div className="rounded-lg border border-border-light bg-white p-4">
      {/* Title */}
      <div className="mb-3 text-sm font-medium text-foreground">
        {criterionName}
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-col gap-2">
        {/* Average score row */}
        <div className="flex items-center gap-2">
          {isLoading ? (
            <div className={skeletonStyle({ class: "h-6 w-20 rounded-sm" })} />
          ) : (
            <CriterionAverageScore
              valueType={gradingValueType}
              value={effectiveAverageScore}
              size={Size.MEDIUM}
            />
          )}

          {isLoading ? (
            <div className={skeletonStyle({ class: "h-5 w-10 rounded-sm" })} />
          ) : (
            effectiveDelta != null &&
            (() => {
              const flatThreshold = gradingValueType === "yes_no" ? 0.01 : 0.1;
              const isFlat = Math.abs(effectiveDelta) < flatThreshold;
              return (
                <span
                  className={twMerge(
                    "text-sm font-medium",
                    isFlat
                      ? "text-muted-foreground"
                      : effectiveDelta > 0
                        ? "text-success"
                        : "text-red-500",
                  )}
                >
                  {isFlat
                    ? "flat"
                    : gradingValueType === "yes_no"
                      ? `${effectiveDelta > 0 ? "+" : ""}${Math.round(effectiveDelta * 100)} pts`
                      : `${effectiveDelta > 0 ? "+" : ""}${effectiveDelta.toFixed(1)}`}
                </span>
              );
            })()
          )}
        </div>

        {/* Category filters and period row */}
        <div className="flex items-center gap-2">
          {/* Improving button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("improving")}
            className={twMerge(
              "shrink-0 font-normal transition-opacity",
              !visibleCategories.improving && "opacity-50",
            )}
          >
            <span className="inline-flex items-center">
              {categoryStats.improving.count}
              <IconArrowUpRight className="mx-0.5 h-3 w-3 text-success" />
              {categoryStats.improving.count > 0 &&
                ` (${formatAvgDelta(categoryStats.improving.avgDelta)})`}
            </span>
          </Button>

          {/* Flat button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("flat")}
            className={twMerge(
              "shrink-0 font-normal transition-opacity",
              !visibleCategories.flat && "opacity-50",
            )}
          >
            <span>{categoryStats.flat.count} flat</span>
          </Button>

          {/* Declining button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("declining")}
            className={twMerge(
              "shrink-0 font-normal transition-opacity",
              !visibleCategories.declining && "opacity-50",
            )}
          >
            <span className="inline-flex items-center">
              {categoryStats.declining.count}
              <IconArrowDownRight className="mx-0.5 h-3 w-3 text-red-500" />
              {categoryStats.declining.count > 0 &&
                ` (${formatAvgDelta(categoryStats.declining.avgDelta)})`}
            </span>
          </Button>

          <div className="flex-1" />

          {showPeriodDropdown && (
            <SimpleSelect
              label="Period"
              isLabelHidden
              options={PERIOD_OPTIONS}
              value={selectedPeriod}
              onChange={handleSelectedPeriodChange}
            />
          )}
        </div>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="relative h-80 w-full">
          <ChartSkeleton />
        </div>
      ) : (
        <ScoreMovementScatterChart
          points={chartPoints}
          axisMin={axisMin}
          axisMax={axisMax}
          axisTicks={axisTicks}
          formatAxisLabel={(tick) =>
            formatScoreAxisLabel(tick, gradingValueType)
          }
          xAxisTitle="Starting score"
          yAxisTitle="Current score"
        />
      )}
    </div>
  );
};
